import { queryOptions, useSuspenseQuery } from '@tanstack/react-query'

import { equityMarketDataFetcher } from '@/libs/fetcher/presets'
import { StockLastPriceInfo, StockPrices } from '@/types/stock'

interface ChartPricePoint {
  price: string
  timestamp: number
}

export interface FetchStockPricesByIdPayload {
  assetId: string | number
  interval: string
}

const transformToStockPrices = (data: ChartPricePoint[] | null): StockPrices => {
  if (!data || data.length === 0) {
    return {
      price_change: 0,
      usd_price_change: 0,
      prices: [],
      overnight_usd_price: 0,
      overnight_usd_price_change: 0,
    }
  }

  const prices: [number, number, number][] = data.map(point => [
    Math.floor(point.timestamp / 1000),
    parseFloat(point.price) || 0,
    0,
  ])

  const firstPrice = prices[0]?.[1] || 0
  const lastPrice = prices[prices.length - 1]?.[1] || 0
  const priceChange = firstPrice > 0 ? (lastPrice - firstPrice) / firstPrice : 0

  return {
    prices,
    price_change: priceChange,
    usd_price_change: priceChange,
    overnight_usd_price: 0,
    overnight_usd_price_change: 0,
  }
}

const fetchStockChartById = async ({
  assetId,
  interval,
}: FetchStockPricesByIdPayload): Promise<StockPrices> => {
  const data = await equityMarketDataFetcher<ChartPricePoint[]>(
    `/chart`,
    {
      queries: {
        asset_id: assetId,
        interval,
      },
    },
    {
      forceResponseType: 'json',
    },
  )

  return transformToStockPrices(data)
}

export const stockPricesByIdQueryOptions = (payload: FetchStockPricesByIdPayload) =>
  queryOptions({
    queryKey: ['stockPricesById', payload],
    queryFn: () => fetchStockChartById(payload),
  })

export const useStockPricesById = (payload: FetchStockPricesByIdPayload) =>
  useSuspenseQuery(stockPricesByIdQueryOptions(payload))

export interface FetchStockLastPricePayload {
  assetId: string | number
}

const fetchStockLastPrice = async ({
  assetId,
}: FetchStockLastPricePayload): Promise<StockLastPriceInfo> => {
  const data = await equityMarketDataFetcher<StockLastPriceInfo>(
    `/price/${assetId}`,
    {},
    {
      forceResponseType: 'json',
    },
  )

  return data
}

export const stockLastPriceQueryOptions = (payload: FetchStockLastPricePayload) =>
  queryOptions({
    queryKey: ['stockLastPrice', payload],
    queryFn: () => fetchStockLastPrice(payload),
  })

export const useStockLastPrice = (payload: FetchStockLastPricePayload) =>
  useSuspenseQuery(stockLastPriceQueryOptions(payload))
